import { useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useGallery, useContentToken } from '@vibesites/runtime/react';
import {
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/** Character threshold before the blurb is clamped with a Read More control. */
const BLURB_CLAMP_LENGTH = 200;

/* ---------------------------------------------------------------------------
 * Smart photo selection
 * The three cards default to photo_gallery[0..2], but a property's gallery is
 * often ordered so that logos, floor-plan schematics, bathrooms, laundry rooms,
 * and other low-appeal shots land in those slots. To keep the section
 * attractive before AI generation runs, we score the gallery at render time:
 * hard-exclude anything that shouldn't create a first impression (logos,
 * signage, floor plans, bathrooms, laundry, utility, parking) plus honor the
 * category conventions the Gallery page uses (title = "logo" | "floorplan" |
 * "other" is never a viable pick). Then we pick per CTA:
 *   - Card 1 "Spacious Layouts"     → interior apartment shots
 *   - Card 2 "Picture Yourself Here" → lifestyle (interior or community)
 *   - Card 3 "Convenience All Around" → community amenities / exterior
 * A manual editor override always wins over the smart pick.
 * ------------------------------------------------------------------------- */

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  alt_text?: string | null;
  description?: string | null;
  original_url?: string | null;
  source_page_urls?: string | null;
}

/**
 * Titles the Gallery page treats as non-photo categories. If the source data
 * classifies a photo as one of these, we never surface it here either.
 */
const EXCLUDED_TITLES = new Set(['logo', 'floorplan', 'floor plan', 'other']);

/**
 * Substrings that disqualify a photo from being a first-impression image.
 * Covers the low-quality / low-appeal cases the user called out: logos,
 * signage, floor-plan schematics, bathrooms, laundry, utility, parking.
 */
const EXCLUDE_KEYWORDS = [
  // Branding / non-photos
  'logo', 'signage', 'wordmark', 'brandmark', 'watermark',
  // Floor plans and site maps
  'floorplan', 'floor plan', 'siteplan', 'site plan', 'schematic', 'diagram',
  // Bathrooms / laundry / closets / utility
  'bath', 'restroom', 'toilet', 'shower', 'tub', 'vanity',
  'laundry', 'washer', 'dryer', 'utility', 'mechanical', 'closet', 'hvac',
  // Parking / trash / back-of-house
  'parking', 'garage', 'trash', 'dumpster', 'storage', 'maintenance',
];

const INTERIOR_KEYWORDS = [
  'apartment', 'unit', 'interior', 'model', 'home', 'suite', 'residence',
  'living', 'living room', 'family room', 'great room',
  'kitchen', 'kitchen island', 'countertop', 'cabinetry', 'finishes',
  'bedroom', 'primary bedroom', 'master bedroom',
  'dining', 'dining room', 'open concept', 'floor-to-ceiling',
];

const INDOOR_AMENITY_KEYWORDS = [
  'clubhouse', 'club house', 'lounge', 'fitness', 'gym', 'business center',
  'coworking', 'co-work', 'game', 'media', 'cyber', 'coffee', 'resident',
  'common', 'theater', 'yoga', 'wellness', 'spa',
];

const COMMUNITY_KEYWORDS = [
  'pool', 'poolside', 'courtyard', 'exterior', 'building', 'facade',
  'grounds', 'landscap', 'patio', 'grill', 'rooftop', 'deck', 'outdoor',
  'entrance', 'community', 'park', 'playground', 'dog', 'cabana',
  'fire pit', 'firepit', 'lawn', 'terrace', 'balcony',
];

function normalizedTitle(p: GalleryPhoto): string {
  return (p.title || '').toLowerCase().trim();
}

function photoText(p: GalleryPhoto): string {
  return [p.title, p.description, p.alt_text, p.image, p.original_url, p.source_page_urls]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function isExcludedPhoto(p: GalleryPhoto): boolean {
  if (EXCLUDED_TITLES.has(normalizedTitle(p))) return true;
  const text = photoText(p);
  return EXCLUDE_KEYWORDS.some((kw) => text.includes(kw));
}

/**
 * Photo category from the source `title` field. Matches the Gallery page's
 * `classifyPhoto` helper so both surfaces stay consistent.
 */
function photoCategory(p: GalleryPhoto): 'apartment' | 'community' | 'other' {
  const title = normalizedTitle(p);
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return 'other';
}

function keywordScore(p: GalleryPhoto, keywords: string[]): number {
  const text = photoText(p);
  return keywords.reduce((sum, kw) => sum + (text.includes(kw) ? 1 : 0), 0);
}

/**
 * Composite score for a card. A matching source category is worth a large
 * bump (weight 3) so a photo the property model already labels "apartment"
 * beats a random shot with one keyword hit. Keyword hits refine the ordering
 * within a category.
 */
function scoreForCard(
  p: GalleryPhoto,
  keywords: string[],
  preferredCategory: 'apartment' | 'community' | null,
): number {
  const kw = keywordScore(p, keywords);
  const cat = preferredCategory && photoCategory(p) === preferredCategory ? 3 : 0;
  return kw + cat;
}

/** Pick the highest-scoring unused, non-excluded photo for a category. */
function pickPhoto(
  gallery: GalleryPhoto[],
  keywords: string[],
  preferredCategory: 'apartment' | 'community' | null,
  used: Set<string>,
): GalleryPhoto | null {
  let best: GalleryPhoto | null = null;
  let bestScore = 0;
  for (const p of gallery) {
    if (!p.image || used.has(p.image) || isExcludedPhoto(p)) continue;
    const score = scoreForCard(p, keywords, preferredCategory);
    if (score > bestScore) {
      best = p;
      bestScore = score;
    }
  }
  // Fallback: no keyword/category match — take the first unused, non-excluded photo.
  if (!best) {
    best = gallery.find((p) => p.image && !used.has(p.image) && !isExcludedPhoto(p)) ?? null;
  }
  if (best?.image) used.add(best.image);
  return best;
}

/**
 * Resolve a card's image: honor a manual/custom editor pick, otherwise fall
 * back to the smart pick. A slot value that resolves to an excluded gallery
 * photo (the raw default ordering — a logo, floor plan, bathroom, etc.) is
 * treated as "not chosen" and replaced with the smart pick.
 */
function resolveCardImage(
  slotUrl: string | null | undefined,
  slotAlt: string | null | undefined,
  smart: GalleryPhoto | null,
  gallery: GalleryPhoto[],
): { image: string; alt: string } {
  if (slotUrl) {
    const match = gallery.find((p) => p.image === slotUrl || p.original_url === slotUrl);
    // Custom upload (not in gallery) or an acceptable gallery photo → respect it.
    if (!match || !isExcludedPhoto(match)) {
      return { image: slotUrl, alt: slotAlt || match?.alt_text || '' };
    }
  }
  return { image: smart?.image ?? slotUrl ?? '', alt: smart?.alt_text ?? '' };
}

const card1ImageDef: SlotGroupDefinition = {
  label: 'Card 1 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a strong interior photo that showcases the apartment homes — a living room, bedroom, kitchen, dining area, or a shot that highlights layouts and finishes. Only use high-quality, desirable images that create a strong first impression. Do not use logos, signage, floor-plan schematics, bathrooms, laundry rooms, closets, parking, utility/mechanical/back-of-house spaces, or any blurry, dirty, or unappealing shots.',
    context:
      'Left of three feature cards under the "Spacious Layouts" heading; the image should feel like a hero-quality interior. Each of the three cards must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Card 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select an appealing lifestyle photo — either an inviting interior (living space, model unit, resident lounge, clubhouse) or a community space (pool, courtyard, rooftop) that shows what daily life at the property feels like. Only use high-quality, desirable images that create a strong first impression. Do not use logos, signage, floor-plan schematics, bathrooms, laundry rooms, closets, parking, utility/mechanical/back-of-house spaces, or any blurry, dirty, or unappealing shots.',
    context:
      'Middle of three feature cards under the "Picture Yourself Here" heading; the image should evoke lifestyle and belonging. Each of the three cards must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Card 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a strong community photo that highlights amenities, outdoor spaces, or property features — a pool, courtyard, rooftop deck, grill area, dog park, playground, fire pit, cabana, or a hero exterior/building shot. Only use high-quality, desirable images that create a strong first impression. Do not use logos, signage, floor-plan schematics, bathrooms, laundry rooms, closets, parking, utility/mechanical/back-of-house spaces, or any blurry, dirty, or unappealing shots.',
    context:
      'Right of three feature cards under the "Convenience All Around" heading; the image should showcase community amenities and outdoor property features. Each of the three cards must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const card1CtaDef: SlotGroupDefinition = {
  label: 'Card 1 CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Find Your Space' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card2CtaDef: SlotGroupDefinition = {
  label: 'Card 2 CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View The Gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card3CtaDef: SlotGroupDefinition = {
  label: 'Card 3 CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Browse Our Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function PropertyOverview() {
  const card1Image = useSlotGroup('propertyoverview.card1_image', card1ImageDef);
  const card2Image = useSlotGroup('propertyoverview.card2_image', card2ImageDef);
  const card3Image = useSlotGroup('propertyoverview.card3_image', card3ImageDef);

  const card1Cta = useSlotGroup('propertyoverview.card1_cta', card1CtaDef);
  const card2Cta = useSlotGroup('propertyoverview.card2_cta', card2CtaDef);
  const card3Cta = useSlotGroup('propertyoverview.card3_cta', card3CtaDef);

  const blurb = useContentToken('propertyoverview.blurb');
  const [blurbExpanded, setBlurbExpanded] = useState(false);
  const isLongBlurb = !!blurb && String(blurb).length > BLURB_CLAMP_LENGTH;

  const gallery = useGallery() as GalleryPhoto[];

  const cardImages = useMemo(() => {
    const used = new Set<string>();
    // Card 1 "Spacious Layouts" — apartment interior shots.
    const smart1 = pickPhoto(gallery, INTERIOR_KEYWORDS, 'apartment', used);
    // Card 2 "Picture Yourself Here" — lifestyle: interior amenity + community
    // keywords blended so either a resident lounge or a courtyard can win.
    const smart2 = pickPhoto(
      gallery,
      [...INDOOR_AMENITY_KEYWORDS, ...COMMUNITY_KEYWORDS],
      null,
      used,
    );
    // Card 3 "Convenience All Around" — community amenities / exterior.
    const smart3 = pickPhoto(gallery, COMMUNITY_KEYWORDS, 'community', used);
    return {
      card1: resolveCardImage(card1Image.image, card1Image.alt, smart1, gallery),
      card2: resolveCardImage(card2Image.image, card2Image.alt, smart2, gallery),
      card3: resolveCardImage(card3Image.image, card3Image.alt, smart3, gallery),
    };
  }, [
    gallery,
    card1Image.image, card1Image.alt,
    card2Image.image, card2Image.alt,
    card3Image.image, card3Image.alt,
  ]);

  useRevealStyles();
  const cardsRef = useScrollReveal<HTMLDivElement>();
  const copyRef = useScrollReveal<HTMLDivElement>();

  // Load Montserrat (Light 300) for the blurb copy. Guarded so repeat mounts
  // don't inject duplicate <link> tags, and reuses the same data attribute
  // pattern the Rubik loader in Header/Hero uses.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="montserrat"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300&display=swap';
    link.setAttribute('data-vibe-font', 'montserrat');
    document.head.appendChild(link);
  }, []);

  return (
    <section className="po-section">
      <div className="po-inner">
        <div ref={cardsRef} className="po-cards reveal-fade-up">
          {/* Card 1 */}
          <div className="po-card">
            <EditableSlotGroup groupPath="propertyoverview.card1_image" definition={card1ImageDef}>
              <div className="po-card-media">
                <VibesitesImage
                  url={cardImages.card1.image}
                  alt={cardImages.card1.alt}
                  className="po-card-img"
                  sizes="(min-width: 1024px) 22vw, (min-width: 640px) 33vw, 100vw"
                />
              </div>
            </EditableSlotGroup>
            <div className="po-card-scrim" aria-hidden="true" />
            <div className="po-card-content">
              <RichTextContent
                path="propertyoverview.card1_heading"
                as="h2"
                className="po-card-heading font-heading"
              />
              <EditableSlotGroup
                groupPath="propertyoverview.card1_cta"
                definition={card1CtaDef}
                className="po-card-cta-wrap"
              >
                <a
                  href={card1Cta.url || '/floor-plans'}
                  target={card1Cta._target || '_self'}
                  className="po-card-caption"
                >
                  {card1Cta.label || 'Find Your Space'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Card 2 */}
          <div className="po-card">
            <EditableSlotGroup groupPath="propertyoverview.card2_image" definition={card2ImageDef}>
              <div className="po-card-media">
                <VibesitesImage
                  url={cardImages.card2.image}
                  alt={cardImages.card2.alt}
                  className="po-card-img"
                  sizes="(min-width: 1024px) 22vw, (min-width: 640px) 33vw, 100vw"
                />
              </div>
            </EditableSlotGroup>
            <div className="po-card-scrim" aria-hidden="true" />
            <div className="po-card-content">
              <RichTextContent
                path="propertyoverview.card2_heading"
                as="h2"
                className="po-card-heading font-heading"
              />
              <EditableSlotGroup
                groupPath="propertyoverview.card2_cta"
                definition={card2CtaDef}
                className="po-card-cta-wrap"
              >
                <a
                  href={card2Cta.url || '/gallery'}
                  target={card2Cta._target || '_self'}
                  className="po-card-caption"
                >
                  {card2Cta.label || 'View The Gallery'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Card 3 */}
          <div className="po-card">
            <EditableSlotGroup groupPath="propertyoverview.card3_image" definition={card3ImageDef}>
              <div className="po-card-media">
                <VibesitesImage
                  url={cardImages.card3.image}
                  alt={cardImages.card3.alt}
                  className="po-card-img"
                  sizes="(min-width: 1024px) 22vw, (min-width: 640px) 33vw, 100vw"
                />
              </div>
            </EditableSlotGroup>
            <div className="po-card-scrim" aria-hidden="true" />
            <div className="po-card-content">
              <RichTextContent
                path="propertyoverview.card3_heading"
                as="h2"
                className="po-card-heading font-heading"
              />
              <EditableSlotGroup
                groupPath="propertyoverview.card3_cta"
                definition={card3CtaDef}
                className="po-card-cta-wrap"
              >
                <a
                  href={card3Cta.url || '/amenities'}
                  target={card3Cta._target || '_self'}
                  className="po-card-caption"
                >
                  {card3Cta.label || 'Browse Our Amenities'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>

        <div ref={copyRef} className="po-copy reveal-fade-up">
          <RichTextContent
            path="propertyoverview.title"
            as="h2"
            className="po-title font-heading"
          />
          <div
            className={`po-blurb-wrap${
              blurbExpanded || !isLongBlurb ? ' po-blurb-wrap--open' : ''
            }`}
          >
            <RichTextContent
              path="propertyoverview.blurb"
              as="div"
              className="po-blurb font-body"
            />
          </div>
          {isLongBlurb && (
            <button
              type="button"
              onClick={() => setBlurbExpanded((v) => !v)}
              aria-expanded={blurbExpanded}
              className="po-read-more"
            >
              {blurbExpanded ? (
                <RichTextContent path="propertyoverview.read_less_label" as="span" />
              ) : (
                <RichTextContent path="propertyoverview.read_more_label" as="span" />
              )}
            </button>
          )}
        </div>
      </div>

      <style>{`
        .po-section {
          position: relative;
          isolation: isolate;
          background: var(--vibe-color-background);
          padding: 3rem 1.25rem 4.5rem;
        }

        @media (min-width: 640px) {
          .po-section {
            padding-top: 3.5rem;
          }
        }

        .po-inner {
          max-width: 1400px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: start;
        }

        /* Desktop: three image cards share the left column, copy sits on the
           right. The cards' column takes ~2/3 of the width so the trio still
           reads as the hero, while the copy has enough room for a comfortable
           reading measure. */
        @media (min-width: 1024px) {
          .po-inner {
            grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
            gap: 3rem;
          }
        }

        .po-cards {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
        }

        @media (min-width: 640px) {
          .po-cards {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        .po-card {
          position: relative;
          overflow: hidden;
          aspect-ratio: 4 / 5;
          background: var(--vibe-color-surface);
          border-radius: 10px;
        }

        .po-card-media {
          position: absolute;
          inset: 0;
        }

        .po-card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .po-card:hover .po-card-img {
          transform: scale(1.06);
        }

        .po-card-scrim {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.15) 0%,
            rgba(0, 0, 0, 0.35) 55%,
            rgba(0, 0, 0, 0.55) 100%
          );
          pointer-events: none;
        }

        .po-card-content {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 1.5rem;
          text-align: center;
        }

        .po-card-heading {
          margin: 0;
          font-size: clamp(1.35rem, 2.4vw, 1.9rem);
          line-height: 1.1;
          font-weight: 400;
          text-transform: none;
          text-align: center;
          color: #ffffff;
          text-shadow: 0 2px 12px rgba(0, 0, 0, 0.45);
        }

        .po-card-cta-wrap {
          display: block;
        }

        /* Plain caption text under each heading — no button chrome. Sits on
           the scrim so the white keeps contrast against the photo. A stretched
           ::after makes the whole card follow the CTA (relative to
           .po-card-content, which already covers the card). */
        .po-card-caption {
          display: inline-block;
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.3;
          text-align: center;
          color: #ffffff;
          text-decoration: none;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
          transition: opacity 0.2s ease;
        }

        .po-card-caption::after {
          content: '';
          position: absolute;
          inset: 0;
        }

        .po-card:hover .po-card-caption {
          opacity: 0.85;
          text-decoration: underline;
        }

        /* Tablet: cards go 3-across but are narrow, so scale down the heading
           and caption to keep content from wrapping/overflowing. */
        @media (min-width: 640px) and (max-width: 1023px) {
          .po-card-content {
            gap: 0.5rem;
            padding: 1rem;
          }

          .po-card-heading {
            font-size: clamp(1.05rem, 2.6vw, 1.4rem);
          }

          .po-card-caption {
            font-size: 0.9rem;
          }
        }

        .po-copy {
          text-align: left;
        }

        .po-title {
          margin: 0 0 1.5rem;
          font-size: clamp(1.75rem, 3.4vw, 2.5rem);
          line-height: 1.15;
          font-weight: 400;
          letter-spacing: 0;
          text-transform: none;
          color: var(--vibe-color-primary, var(--vibe-color-text));
        }

        /* Clamp long blurbs with a fade, then expand via Read More — same
           interaction as Belmont's overview description, styled for Comfy. */
        .po-blurb-wrap {
          max-width: 34rem;
          max-height: 9.6em;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
        }

        .po-blurb-wrap--open {
          max-height: 60em;
          -webkit-mask-image: none;
          mask-image: none;
        }

        /* Match Home body copy (AmenitiesList / text-base sm:text-lg
           leading-relaxed): 1rem → 1.125rem with an open reading measure. */
        .po-blurb {
          margin: 0;
          font-family: 'Montserrat', var(--vibe-font-body, sans-serif);
          font-size: 1rem;
          font-weight: 300;
          line-height: 1.7;
          color: var(--vibe-color-text);
        }

        @media (min-width: 640px) {
          .po-blurb {
            font-size: 1.125rem;
          }
        }

        /* RichTextContent renders each paragraph inside .po-blurb. Re-declare
           type on <p> so global/editor resets (often font-size ~14px and
           line-height 1) can't undersize or crush the body vs. other blocks. */
        .po-blurb p {
          margin: 0 0 1rem;
          font-family: inherit;
          font-size: inherit;
          font-weight: inherit;
          line-height: 1.7;
        }

        .po-blurb p:last-child {
          margin-bottom: 0;
        }

        .po-read-more {
          display: inline-block;
          margin-top: 0.75rem;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
          font-family: 'Montserrat', var(--vibe-font-body, sans-serif);
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          line-height: 1.4;
          color: var(--vibe-color-primary, var(--vibe-color-text));
          transition: opacity 0.2s ease;
        }

        .po-read-more:hover {
          opacity: 0.75;
          text-decoration: underline;
        }

        .po-read-more p {
          margin: 0;
          font: inherit;
          color: inherit;
        }

        /* On mobile/tablet the copy sits below the cards and centers to keep
           the eye moving down through the section. */
        @media (max-width: 1023px) {
          .po-copy {
            text-align: center;
          }

          .po-blurb-wrap {
            margin-left: auto;
            margin-right: auto;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Embrace a Life of Connection in Your New Apartment Community',
    },
    'propertyoverview.blurb': {
      label: 'Blurb',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue:
        "<p>Our spacious apartments blend quality, comfort, and convenience in a central location with everything you need at your doorstep.</p><p>Choose from brand-new floor plans featuring modern kitchens and retreat-inspired amenities.</p>",
    },
    'propertyoverview.read_more_label': {
      label: 'Read More Label',
      type: 'richtext',
      defaultValue: '<p>Read More</p>',
    },
    'propertyoverview.read_less_label': {
      label: 'Read Less Label',
      type: 'richtext',
      defaultValue: '<p>Read Less</p>',
    },
    'propertyoverview.card1_heading': {
      label: 'Card 1 Heading',
      type: 'richtext',
      defaultValue: 'Spacious Layouts',
    },
    'propertyoverview.card2_heading': {
      label: 'Card 2 Heading',
      type: 'richtext',
      defaultValue: 'Picture Yourself Here',
    },
    'propertyoverview.card3_heading': {
      label: 'Card 3 Heading',
      type: 'richtext',
      defaultValue: 'Convenience All Around',
    },
  },
  slotGroups: {
    'propertyoverview.card1_image': card1ImageDef,
    'propertyoverview.card2_image': card2ImageDef,
    'propertyoverview.card3_image': card3ImageDef,
    'propertyoverview.card1_cta': card1CtaDef,
    'propertyoverview.card2_cta': card2CtaDef,
    'propertyoverview.card3_cta': card3CtaDef,
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
